import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { formatApiErrorDetail, useAuth } from '../context/AuthContext';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { formatInr } from '../utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const PRODUCT_CATEGORIES = ['Necklaces', 'Earrings', 'Bridal Collection'];

const emptyFormData = {
  name: '',
  description: '',
  price: '',
  category: PRODUCT_CATEGORIES[0],
  image_url: '',
  image_urls: [],
  video_urls: [],
  stock: ''
};

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/products');
      return;
    }
    fetchProducts();
  }, [user, navigate, fetchProducts]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.image_urls.length) {
      toast.error('Please upload at least one product image');
      return;
    }

    try {
      const productData = {
        ...formData,
        image_url: formData.image_urls[0],
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock, 10)
      };

      if (editingProduct) {
        await axios.put(
          `${BACKEND_URL}/api/products/${editingProduct.id}`,
          productData,
          { withCredentials: true }
        );
        toast.success('Product updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/products`,
          productData,
          { withCredentials: true }
        );
        toast.success('Product created successfully');
      }

      setShowForm(false);
      setEditingProduct(null);
      setFormData(emptyFormData);
      fetchProducts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save product');
    }
  };

  const handleMediaUpload = async (event, mediaType) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const formPayload = new FormData();
    files.forEach((file) => formPayload.append('files', file));

    setIsUploadingMedia(true);

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/uploads/${mediaType === 'image' ? 'images' : 'videos'}`,
        formPayload,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData((current) => {
        const fieldName = mediaType === 'image' ? 'image_urls' : 'video_urls';
        const nextItems = [...current[fieldName], ...(data.urls || [])];
        const nextImages = mediaType === 'image' ? nextItems : current.image_urls;

        return {
          ...current,
          [fieldName]: nextItems,
          image_url: nextImages[0] || ''
        };
      });
      toast.success(`${mediaType === 'image' ? 'Images' : 'Videos'} uploaded successfully`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || `Failed to upload ${mediaType}`);
    } finally {
      setIsUploadingMedia(false);
      event.target.value = '';
    }
  };

  const removeMediaItem = (mediaType, indexToRemove) => {
    setFormData((current) => {
      const fieldName = mediaType === 'image' ? 'image_urls' : 'video_urls';
      const nextItems = current[fieldName].filter((_, index) => index !== indexToRemove);
      const nextImages = mediaType === 'image' ? nextItems : current.image_urls;

      return {
        ...current,
        [fieldName]: nextItems,
        image_url: nextImages[0] || ''
      };
    });
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      image_url: product.image_url,
      image_urls: product.image_urls?.length ? product.image_urls : (product.image_url ? [product.image_url] : []),
      video_urls: product.video_urls || [],
      stock: product.stock.toString()
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData(emptyFormData);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 py-12 px-6 max-w-7xl mx-auto w-full">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-heading font-light tracking-tight text-[#8C1B2F]" data-testid="admin-title">Admin Panel</h1>
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#8C1B2F] text-white hover:bg-[#6C1322] rounded-sm"
              data-testid="add-product-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Product
            </Button>
          )}
        </div>

        {showForm && (
          <div className="bg-white border border-[#E6E2D6] rounded-sm p-6 mb-8" data-testid="product-form">
            <h2 className="text-2xl font-heading mb-6">{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="product-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="product-category-tabs">
                    {PRODUCT_CATEGORIES.map((category) => {
                      const isActive = formData.category === category;
                      return (
                        <button
                          key={category}
                          type="button"
                          onClick={() => setFormData({ ...formData, category })}
                          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                            isActive
                              ? 'border-[#8C1B2F] bg-[#8C1B2F] text-white'
                              : 'border-[#E6E2D6] bg-white text-[#5C5C5C] hover:border-[#8C1B2F] hover:text-[#8C1B2F]'
                          }`}
                          data-testid={`product-category-tab-${category.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {category}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-[#5C5C5C]" data-testid="product-category-selected">
                    Selected category: {formData.category}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="mt-2"
                  rows={3}
                  data-testid="product-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">Price (INR)</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="product-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="stock">Stock</Label>
                  <Input
                    id="stock"
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    required
                    className="mt-2"
                    data-testid="product-stock-input"
                  />
                </div>
                <div>
                  <Label htmlFor="image_upload">Product Images</Label>
                  <Input
                    id="image_upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleMediaUpload(e, 'image')}
                    className="mt-2"
                    data-testid="product-image-input"
                  />
                  <p className="mt-2 text-xs text-[#5C5C5C]">
                    Upload one or more image files from your device.
                  </p>
                </div>
                <div>
                  <Label htmlFor="video_upload">Product Videos</Label>
                  <Input
                    id="video_upload"
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={(e) => handleMediaUpload(e, 'video')}
                    className="mt-2"
                    data-testid="product-video-input"
                  />
                  <p className="mt-2 text-xs text-[#5C5C5C]">
                    Upload one or more video files from your device.
                  </p>
                </div>
              </div>

              {!!formData.image_urls.length && (
                <div>
                  <Label>Image Gallery</Label>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.image_urls.map((imageUrl, index) => (
                      <div key={`${imageUrl.slice(0, 32)}-${index}`} className="space-y-2">
                        <img
                          src={imageUrl}
                          alt={`Product preview ${index + 1}`}
                          className="h-28 w-full rounded-sm border border-[#E6E2D6] object-cover"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full rounded-sm"
                          onClick={() => removeMediaItem('image', index)}
                        >
                          Remove Image
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!!formData.video_urls.length && (
                <div>
                  <Label>Video Gallery</Label>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.video_urls.map((videoUrl, index) => (
                      <div key={`${videoUrl.slice(0, 32)}-${index}`} className="space-y-2">
                        <video
                          src={videoUrl}
                          controls
                          className="h-40 w-full rounded-sm border border-[#E6E2D6] bg-black object-cover"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full rounded-sm"
                          onClick={() => removeMediaItem('video', index)}
                        >
                          Remove Video
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={isUploadingMedia}
                  className="bg-[#8C1B2F] text-white hover:bg-[#6C1322] rounded-sm"
                  data-testid="save-product-btn"
                >
                  {isUploadingMedia ? 'Uploading Media...' : editingProduct ? 'Update Product' : 'Add Product'}
                </Button>
                <Button type="button" onClick={handleCancel} variant="outline" className="rounded-sm" data-testid="cancel-product-btn">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white border border-[#E6E2D6] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead className="bg-[#F3F0E6]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#1A1A1A] uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#1A1A1A] uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#1A1A1A] uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#1A1A1A] uppercase tracking-wider">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#1A1A1A] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E6E2D6]">
                {products.map((product) => (
                  <tr key={product.id} data-testid={`product-row-${product.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img src={product.image_urls?.[0] || product.image_url} alt={product.name} className="h-12 w-12 object-cover rounded-sm mr-4" />
                        <span className="font-medium">{product.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#5C5C5C]">{product.category}</td>
                    <td className="px-6 py-4 text-sm">{formatInr(product.price)}</td>
                    <td className="px-6 py-4 text-sm">{product.stock}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil className="h-4 w-4 text-[#8C1B2F]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-[#8C1B2F]" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Admin;
